// src/hooks/useChat.ts
import { useState, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  collection,
  doc,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  getDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { Message, Location, ContentPart } from "../types";
import { encodeGeohash } from "../utils/locationUtils";
import {
  useConversationStore,
  ConversationType,
} from "../stores/conversationStore";
import { useStreamingChat } from "./useStreamingChat";
import { useLanguagePreference } from "../i18n/useLanguagePreference";
import { isAudioSupportedLanguage } from "../i18n/languages";

interface LandmarkVerification {
  decision: "verified" | "uncertain" | "not_landmark";
  confidence: number;
  landmark_name?: string;
  landmark_type?:
    | "monument"
    | "museum"
    | "natural"
    | "religious"
    | "architectural"
    | "cultural"
    | "historical"
    | "other";
  reasoning?: string;
  coordinates_match?: boolean;
  visit_worthy?: boolean;
}

// Pending indicator types for visual feedback
export type PendingIndicatorType =
  | { type: "image"; imagePath: string }
  | { type: "location" }
  | { type: "thinking" }
  | null;

export const useChat = (routeConversationId?: string | null) => {
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [localConversationId, setLocalConversationId] = useState<string | null>(
    null
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastLandmarkVerification, setLastLandmarkVerification] = useState<{
    verification: LandmarkVerification;
    visitSaved: boolean;
    visitId?: string;
  } | null>(null);

  // NEW: Pending indicator for visual feedback
  const [pendingIndicator, setPendingIndicator] =
    useState<PendingIndicatorType>(null);

  const completionHandledRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const listenerSetupRef = useRef<string | null>(null);
  const savedMessageIdsRef = useRef<Set<string>>(new Set());
  // Track server-saved message ID to avoid duplicate client saves
  const serverSavedMessageIdRef = useRef<string | null>(null);
  // Track the final content for audio generation
  const finalContentRef = useRef<string>("");

  // Canonical user language preference — same source the rest of the
  // app reads (translation system, AI prompt, etc.). Held in a ref so
  // `triggerBackgroundAudio`'s callback identity stays stable; the
  // language can change mid-session if the user re-picks.
  const { language: userLanguage } = useLanguagePreference();
  const userLanguageRef = useRef(userLanguage);
  useEffect(() => {
    userLanguageRef.current = userLanguage;
  }, [userLanguage]);

  // Local takes precedence over route. In the normal flow `local` is
  // null while we're viewing `/chat/:id`, so this still resolves to the
  // route id. The case that matters is stale recovery: if the route id
  // points at a deleted/missing conversation, `ensureConversation`
  // creates a new one and stores it in `local`. With local-first, that
  // new id surfaces as `actualConversationId`, the listener attaches to
  // the right doc, and the existing effect in ChatInterface fires
  // `onConversationCreated` to update the URL.
  const actualConversationId = localConversationId || routeConversationId;
  const conversationIdRef = useRef<string | null>(actualConversationId);
  const previousRouteConversationIdRef = useRef<string | null>(
    routeConversationId ?? null
  );

  const {
    createConversation,
    updateConversationMetadata,
    generateConversationTitle,
  } = useConversationStore();

  useEffect(() => {
    conversationIdRef.current = actualConversationId;
  }, [actualConversationId]);

  const resetConversationState = useCallback(() => {
    setMessages([]);
    setError(null);
    setIsLoading(false);
    setLastLandmarkVerification(null);
    setPendingIndicator(null);
    completionHandledRef.current = false;
    serverSavedMessageIdRef.current = null;
    finalContentRef.current = "";
    savedMessageIdsRef.current.clear();
  }, []);

  // Clear local chat state immediately when the routed conversation changes so
  // the UI does not momentarily show the previous conversation's messages.
  useEffect(() => {
    const normalizedRouteId = routeConversationId ?? null;
    if (previousRouteConversationIdRef.current !== normalizedRouteId) {
      resetConversationState();
      setLocalConversationId(null);
      conversationIdRef.current = normalizedRouteId;

      // Ensure any existing listener is torn down before the next one starts
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      listenerSetupRef.current = null;

      previousRouteConversationIdRef.current = normalizedRouteId;
    }
  }, [routeConversationId, resetConversationState]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Helper function to set up the Firestore listener
  const setupMessageListener = useCallback(
    (conversationId: string) => {
      const messagesRef = collection(
        db,
        "conversations",
        conversationId,
        "messages"
      );
      const q = query(messagesRef, orderBy("created_at", "asc"));

      return onSnapshot(
        q,
        (snapshot) => {
          const messagesList: Message[] = [];

          snapshot.forEach((doc) => {
            const data = doc.data();

            let parts: ContentPart[] = [];

            if (data.parts) {
              parts = data.parts;
            } else {
              if (data.content) {
                parts.push({ type: "text", content: data.content });
              }
              if (data.image_url) {
                parts.push({
                  type: "image",
                  url: data.image_url,
                  alt: "Attached image",
                });
              }
              if (data.audio_url) {
                parts.push({ type: "audio", url: data.audio_url });
              }
            }

            // Track saved message IDs
            savedMessageIdsRef.current.add(doc.id);

            messagesList.push({
              id: doc.id,
              role: data.role,
              parts,
              created_at:
                data.created_at instanceof Timestamp
                  ? data.created_at.toDate()
                  : new Date(data.created_at || Date.now()),
              location: data.location,
              metadata: data.metadata,
              audioGenerating: data.audioGenerating,
            });
          });

          setMessages((prevMessages) => {
            if (
              streamingChat.isStreaming &&
              messagesList.length <= prevMessages.length
            ) {
              return prevMessages;
            }

            // Filter out optimistic messages that have been saved
            const filteredPrev = prevMessages.filter((m) => {
              if (!m.id.startsWith("temp_")) return false;
              // Keep optimistic messages not yet in the real list
              return !messagesList.some((rm) => {
                // Match by content
                const rmText = rm.parts?.find(
                  (p) => p.type === "text"
                )?.content;
                const mText = m.parts?.find((p) => p.type === "text")?.content;
                const rmImage = rm.parts?.find((p) => p.type === "image")?.url;
                const mImage = m.parts?.find((p) => p.type === "image")?.url;
                return (
                  (rmText && mText && rmText === mText) ||
                  (rmImage && mImage && rmImage === mImage)
                );
              });
            });

            // Combine: real messages + any optimistic messages not yet saved
            return [...messagesList, ...filteredPrev];
          });

          setIsLoading(false);

          // Clear sending/streaming state if we received new messages while phone was asleep
          if (messagesList.length > 0) {
            const lastMessage = messagesList[messagesList.length - 1];
            if (lastMessage.role === "assistant" && isSending) {
              console.log(
                "📱 Detected completed response after wake, clearing sending state"
              );
              setIsSending(false);
              setPendingIndicator(null);
              completionHandledRef.current = true;
            }
          }
        },
        (error) => {
          console.error("Real-time message listener error:", error);
          setIsLoading(false);
        }
      );
    },
    [isSending]
  );

  // Real-time message listener
  useEffect(() => {
    if (!actualConversationId || !auth.currentUser) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    if (listenerSetupRef.current === actualConversationId) {
      return;
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    setIsLoading(true);
    listenerSetupRef.current = actualConversationId;

    unsubscribeRef.current = setupMessageListener(actualConversationId);

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      listenerSetupRef.current = null;
    };
  }, [actualConversationId, setupMessageListener]);

  // Force reconnect listener when app becomes visible (phone unlocked)
  useEffect(() => {
    if (!actualConversationId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && actualConversationId) {
        console.log("📱 App became visible, refreshing Firestore listener...");

        // Tear down existing listener
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }

        // Small delay to allow network to reconnect
        setTimeout(() => {
          if (actualConversationId && auth.currentUser) {
            console.log("📱 Re-establishing Firestore listener...");
            listenerSetupRef.current = actualConversationId;
            unsubscribeRef.current = setupMessageListener(actualConversationId);
          }
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [actualConversationId, setupMessageListener]);

  const triggerBackgroundAudio = useCallback(
    async (messageId: string, conversationId: string, content: string) => {
      try {
        const authToken = await auth.currentUser?.getIdToken();
        if (!authToken) {
          return;
        }

        // Language source of truth: useLanguagePreference (same hook
        // used by the translation system, language gate, etc.). The
        // wire format here is the BCP-47 primary subtag ("en", "es",
        // "ja") — that is what the backend's resolveLanguage /
        // voiceForLanguage / instructionsForLanguage all key on.
        // `null` means the user has not picked one explicitly; the
        // backend will default to English.
        const langCode = userLanguageRef.current?.code ?? null;

        // If the user has explicitly picked a language and TTS does
        // not cover it, skip generation entirely. We still clear the
        // audioGenerating flag client-side so the UI doesn't spin.
        // (No preference → fall through; backend defaults to English,
        // which is supported.)
        if (langCode && !isAudioSupportedLanguage(langCode)) {
          console.log(
            `🔊 Skipping audio generation — language "${langCode}" is not in the supported set.`
          );
          try {
            await updateDoc(
              doc(
                db,
                "conversations",
                conversationId,
                "messages",
                messageId
              ),
              { audioGenerating: deleteField() }
            );
          } catch (err) {
            console.warn("🔊 Failed to clear audioGenerating flag:", err);
          }
          return;
        }

        const audioFunctionUrl =
          "https://us-central1-travelx-46b7a.cloudfunctions.net/generateAudioBackground";

        fetch(audioFunctionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            messageId,
            conversationId,
            content,
            // Send the BCP-47 code, NOT the englishName. The backend
            // expects codes; sending "Spanish" instead of "es" was the
            // long-standing bug that silently routed every non-English
            // user to the English voice + English style instructions.
            language: langCode ?? undefined,
            auth_token: authToken,
          }),
        })
          .then(async (response) => {
            if (response.ok) {
              const result = await response.json();
              console.log("🔊 Background audio generation successful:", result);
            }
          })
          .catch((error) => {
            console.error("🔊 Background audio network error:", error);
          });
      } catch (error) {
        console.error(
          "🔊 Failed to trigger background audio generation:",
          error
        );
      }
    },
    []
  );

  const handleLandmarkVerified = useCallback(
    (
      verification: LandmarkVerification,
      visitSaved: boolean,
      visitId?: string
    ) => {
      setLastLandmarkVerification({
        verification,
        visitSaved,
        visitId,
      });
    },
    []
  );

  // Handler for when server confirms it saved the assistant message
  const handleAssistantMessageSaved = useCallback(
    (messageId: string) => {
      console.log("📝 Server saved assistant message with ID:", messageId);
      serverSavedMessageIdRef.current = messageId;

      // Trigger background audio generation using the server-saved message ID
      const conversationId = conversationIdRef.current;
      const content = finalContentRef.current;
      if (conversationId && content) {
        triggerBackgroundAudio(messageId, conversationId, content);
      }
    },
    [triggerBackgroundAudio]
  );

  const streamingChat = useStreamingChat({
    onStreamStart: () => {
      completionHandledRef.current = false;
      serverSavedMessageIdRef.current = null;
      finalContentRef.current = "";
      // Clear pending indicator when streaming starts - AI is now responding
      setPendingIndicator(null);
    },
    onToken: (token, fullContent) => {
      // Track the latest content for audio generation
      finalContentRef.current = fullContent;
    },
    onStreamEnd: (fullContent) => {
      finalContentRef.current = fullContent;
    },
    onAudioReady: (audioUrl) => {},
    onLandmarkVerified: handleLandmarkVerified,
    onAssistantMessageSaved: handleAssistantMessageSaved,
    onComplete: async (content, audioUrl) => {
      if (completionHandledRef.current) {
        return;
      }

      completionHandledRef.current = true;
      finalContentRef.current = content;

      const conversationId = conversationIdRef.current;

      if (!conversationId) {
        setIsSending(false);
        setPendingIndicator(null);
        completionHandledRef.current = false;
        return;
      }

      try {
        // Check if server already saved the message
        if (serverSavedMessageIdRef.current) {
          console.log(
            "✅ Server already saved assistant message, skipping client save"
          );
          // Audio generation already triggered in handleAssistantMessageSaved
        } else {
          // Fallback: Server didn't confirm save yet - this can happen if:
          // 1. Using old server code that doesn't send assistant_message_saved
          // 2. The SSE event was missed
          // Save client-side as fallback
          console.log(
            "⚠️ Server did not confirm save, saving assistant message client-side as fallback"
          );
          const assistantMessageId = await saveAssistantMessage(
            content,
            undefined,
            conversationId,
            true
          );

          if (assistantMessageId) {
            triggerBackgroundAudio(assistantMessageId, conversationId, content);
          }
        }

        setIsSending(false);
        setPendingIndicator(null);
      } catch (error) {
        console.error("Error saving assistant message:", error);
        setError(new Error("Failed to save message"));
        setIsSending(false);
        setPendingIndicator(null);
        completionHandledRef.current = false;
      }
    },
    onError: (errorMessage) => {
      console.error("Streaming error:", errorMessage);
      setError(new Error(errorMessage));
      setIsSending(false);
      setPendingIndicator(null);
      completionHandledRef.current = false;
    },
    onStatus: (status) => {},
  });

  const buildMessageLocation = useCallback(
    (loc?: Location | null): Message["location"] | null => {
      if (
        !loc ||
        typeof loc.latitude !== "number" ||
        typeof loc.longitude !== "number"
      ) {
        return null;
      }

      try {
        const geohash = encodeGeohash(loc.latitude, loc.longitude, 6);
        const precision: "coarse" | "medium" | "fine" =
          loc.accuracy !== undefined
            ? loc.accuracy <= 50
              ? "fine"
              : loc.accuracy <= 200
              ? "medium"
              : "coarse"
            : "coarse";

        return { geohash, precision };
      } catch (error) {
        console.warn("Failed to encode message location:", error);
        return null;
      }
    },
    []
  );

  const ensureConversation = useCallback(
    async (
      firstMessage?: string,
      location?: Location,
      initialMode?: ConversationType,
      initialModeData?: Record<string, any>
    ): Promise<{ id: string; created: boolean }> => {
      // If we already created (or recovered) a conversation locally
      // during this hook's lifetime, use that — don't re-check the
      // route id, which we may have already determined was stale.
      if (localConversationId) {
        return { id: localConversationId, created: false };
      }

      if (routeConversationId) {
        // Defensive: the route id can be stale. localStorage's
        // `lastConversationId` outlives the conversation it points at
        // (sidebar delete, GDPR purge, multi-device cleanup, etc.) and
        // RootLandingRoute / ChatRoute redirect to /chat/<id> blindly.
        // Sending to a non-existent conversation fails the messages
        // subcollection rule, which `get()`s the parent and matches
        // user_id. Verify before trusting it.
        try {
          const snap = await getDoc(
            doc(db, "conversations", routeConversationId)
          );
          if (
            snap.exists() &&
            snap.data()?.user_id === auth.currentUser?.uid
          ) {
            return { id: routeConversationId, created: false };
          }
          // Doc exists but isn't ours — extremely unlikely (last id is
          // keyed by uid in localStorage) but treat as stale.
        } catch (err: any) {
          // Only fall through on permission-denied (the read rule
          // denies access because the parent doesn't exist or we don't
          // own it). For network/unavailable errors, keep the route id
          // and let the subsequent write surface the real error rather
          // than orphaning a real conversation by silently creating a
          // new one.
          if (err?.code !== "permission-denied") {
            return { id: routeConversationId, created: false };
          }
        }
        // Fall through: stale, create a new conversation.
      }

      const newConversationId = await createConversation(
        firstMessage,
        location,
        initialMode,
        initialModeData
      );
      setLocalConversationId(newConversationId);

      return { id: newConversationId, created: true };
    },
    [routeConversationId, localConversationId, createConversation]
  );

  const saveAssistantMessage = useCallback(
    async (
      content: string,
      audioUrl?: string,
      conversationId?: string,
      audioGenerating: boolean = false
    ): Promise<string | null> => {
      const targetConversationId = conversationId || actualConversationId;

      if (!targetConversationId) {
        throw new Error("No conversation ID available");
      }

      try {
        const conversationDoc = await getDoc(
          doc(db, "conversations", targetConversationId)
        );
        const conversationData = conversationDoc.data();
        const currentMessageCount = conversationData?.message_count || 0;

        const assistantParts: ContentPart[] = [
          { type: "text", content: content },
        ];

        if (audioUrl) {
          assistantParts.push({
            type: "audio",
            url: audioUrl,
          });
        }

        const assistantMessage = {
          role: "assistant" as const,
          parts: assistantParts,
          created_at: new Date(),
          audioGenerating,
          metadata: {
            model_version: "gpt-5.4",
            processing_time: Date.now(),
          },
        };

        const assistantMessageRef = collection(
          db,
          "conversations",
          targetConversationId,
          "messages"
        );
        const docRef = await addDoc(assistantMessageRef, assistantMessage);

        const updateData: any = {
          message_count: currentMessageCount + 1,
          last_message:
            content.length > 100 ? content.substring(0, 100) + "..." : content,
          updated_at: new Date(),
        };

        if (currentMessageCount === 1) {
          const firstUserMessage = messages.find((m) => m.role === "user");
          if (firstUserMessage) {
            const firstText = firstUserMessage.parts?.find(
              (p) => p.type === "text"
            )?.content;
            if (firstText) {
              updateData.title = generateConversationTitle(firstText);
            }
          }
        }

        await updateConversationMetadata(targetConversationId, updateData);

        return docRef.id;
      } catch (error) {
        console.error("Error saving assistant message:", error);
        throw error;
      }
    },
    [
      actualConversationId,
      messages,
      updateConversationMetadata,
      generateConversationTitle,
    ]
  );

  const addOptimisticMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Enhanced sendMessage with pending indicators
  const sendMessage = useCallback(
    async ({
      message,
      displayMessage,
      imagePath,
      location,
      isLocationRequest = false,
      isImageOnlySubmission = false,
      initialMode,
      initialModeData,
    }: {
      message: string;
      displayMessage?: string;
      imagePath?: string;
      location?: Location;
      isLocationRequest?: boolean;
      isImageOnlySubmission?: boolean;
      /** When the caller is sending the FIRST message in a new
       *  conversation and has picked a mode on the WelcomeScreen,
       *  these flow through to createConversation so the new doc is
       *  born in the right mode + state. Ignored when the
       *  conversation already exists. */
      initialMode?: ConversationType;
      initialModeData?: Record<string, any>;
    }) => {
      if (!auth.currentUser) {
        throw new Error("Not authenticated");
      }

      if (isSending || streamingChat.isStreaming) {
        return;
      }

      try {
        setIsSending(true);
        setError(null);
        completionHandledRef.current = false;
        serverSavedMessageIdRef.current = null;
        finalContentRef.current = "";
        setLastLandmarkVerification(null);

        // SET PENDING INDICATOR based on request type
        if (isLocationRequest) {
          setPendingIndicator({ type: "location" });
        } else if (isImageOnlySubmission && imagePath) {
          setPendingIndicator({ type: "image", imagePath });
        } else {
          setPendingIndicator({ type: "thinking" });
        }

        // Build display parts
        let displayParts: ContentPart[] = [];
        let textToDisplay = "";
        const messageLocation = buildMessageLocation(location);

        if (isLocationRequest) {
          // Location request - no user message displayed, but we show indicator
          textToDisplay = "";
          displayParts = [];
        } else if (isImageOnlySubmission && imagePath) {
          // Image-only - show the image immediately
          textToDisplay = "";
          displayParts = [
            {
              type: "image",
              url: imagePath,
              alt: "User uploaded image",
            },
          ];
        } else if (imagePath && displayMessage) {
          // Image with text
          textToDisplay = displayMessage;
          displayParts = [
            { type: "text", content: displayMessage },
            {
              type: "image",
              url: imagePath,
              alt: "User uploaded image",
            },
          ];
        } else if (displayMessage) {
          // Text only
          textToDisplay = displayMessage;
          displayParts = [{ type: "text", content: displayMessage }];
        }

        // Add optimistic user message if there's content to display
        if (displayParts.length > 0) {
          const optimisticUserMessage: Message = {
            id: `temp_${Date.now()}`,
            role: "user",
            parts: displayParts,
            created_at: new Date(),
          };

          if (messageLocation) {
            optimisticUserMessage.location = messageLocation;
          }

          addOptimisticMessage(optimisticUserMessage);
        }

        const { id: conversationId, created: conversationJustCreated } =
          await ensureConversation(
            textToDisplay,
            location,
            initialMode,
            initialModeData
          );
        conversationIdRef.current = conversationId;

        // Brief wait for Firestore consistency only when we just
        // created the parent conversation (either a true new chat or a
        // stale-route recovery). On normal sends to an existing
        // conversation we skip the wait.
        if (conversationJustCreated) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        // Save user message to Firestore
        if (displayParts.length > 0) {
          const userMessageBase = {
            role: "user" as const,
            parts: displayParts,
            created_at: new Date(),
          };

          const userMessage = messageLocation
            ? { ...userMessageBase, location: messageLocation }
            : userMessageBase;

          const conversationDoc = await getDoc(
            doc(db, "conversations", conversationId)
          );
          const conversationData = conversationDoc.data();
          const currentMessageCount = conversationData?.message_count || 0;

          const userMessageRef = collection(
            db,
            "conversations",
            conversationId,
            "messages"
          );
          await addDoc(userMessageRef, userMessage);

          const updateData: any = {
            message_count: currentMessageCount + 1,
            last_message:
              textToDisplay.length > 100
                ? textToDisplay.substring(0, 100) + "..."
                : textToDisplay || "Photo shared",
            updated_at: new Date(),
          };

          await updateConversationMetadata(conversationId, updateData);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Send to AI
        await streamingChat.sendStreamingMessage(
          message,
          conversationId,
          imagePath,
          location
        );

        // Return the (possibly just-created) conversation id so the
        // caller can use it for follow-up actions like firing
        // generateWalkingTour. Reading actualConversationId from a
        // closure after this await is unreliable — React state may
        // not have flushed yet, and even if it has, useCallback
        // closures capture the value at definition time. Returning
        // the id avoids the entire stale-closure class of bugs.
        return { conversationId };
      } catch (error: any) {
        console.error("Message send failed:", error);
        setError(error);
        setIsSending(false);
        setPendingIndicator(null);
        completionHandledRef.current = false;
        throw error; // Re-throw for retry mechanism
      }
    },
    [
      ensureConversation,
      streamingChat,
      isSending,
      updateConversationMetadata,
      addOptimisticMessage,
      buildMessageLocation,
    ]
  );

  const startNewConversation = useCallback(() => {
    setLocalConversationId(null);
    resetConversationState();
    conversationIdRef.current = null;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    listenerSetupRef.current = null;
  }, [resetConversationState]);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    isSending,
    isStreaming: streamingChat.isStreaming,
    streamingContent: streamingChat.streamingContent,
    streamingStatus: streamingChat.status,
    error:
      error || (streamingChat.error ? new Error(streamingChat.error) : null),
    conversationId: actualConversationId,
    startNewConversation,
    lastLandmarkVerification,
    pendingIndicator,
    clearError,
  };
};
